import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { actions } from 'store/settings';
import { actions as eventActions } from 'store/event';
import { produce } from 'immer';
import set from 'lodash.set';
import merge from 'lodash.merge';
import deviceContextSettingsValidator from '../validator';
import FormController from 'common/formController';
import { setTimeZoneModified, unsetTimeZoneModified } from 'store/settings/actions';

const { updateSettingsMetaData } = actions;
const { getSupportedEventTypes } = eventActions;
let formController = new FormController();

export default function useDeviceContextSettings() {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [products, setProducts] = useState([]);
  const supportedEventTypesStore = useSelector(
    store => store.events.supportedEventTypes.integratingProducts
  );

  const settingsMetaDataStore = useSelector(
    store => store.settings.settingsMetaData.data
  );
  
  const isSettingsMetaDataLoading = useSelector(
    store => store.settings.settingsMetaData.getting
  );

  const isSettingsMetaDataUpdating = useSelector(
    store => store.settings.settingsMetaData.updating
  );

  const isSettingsMetaDataUpdated = useSelector(
    store => store.settings.settingsMetaData.isUpdated
  );

  const currentTimeZone = useSelector(store => store.settings.timeZone);
  const isTimeZoneModified = useSelector(store => store.settings.isTimeZoneModified);

  const [settingsMetaData, setSettingsMetaData] = useState(null);

  const DEFAULT_TIME_ZONE_PATH = 'timeZone';
  const CHECK_IN_TIME = 'checkInTime';
  const CHECK_OUT_TIME = 'checkOutTime';
  const PRODUCT_INTEGRATIONS = 'integratingProducts';
  const CONSENT_PROMOTIONAL = 'enableConsentForPromotionalCampaigns';
  const CONSENT_TRANSACTIONAL = 'enableConsentForTransactionalCampaigns';
  const ENABLE_UTM_TRACKING = 'enableUtmTracking';
  const SMS_COUNTRY_CODE = 'smsCountryCode';
  const LANGUAGE_CODE = 'languageCode';

  useEffect(() => {
    dispatch(getSupportedEventTypes());
  }, []);

  useEffect(() => {
    setIsLoading(isSettingsMetaDataLoading);
  }, [isSettingsMetaDataLoading]);

  const settingsDataModel = Object.freeze({
    sms: {
      defaultSMSCountryCodeInfo: { callingCode: null, countryCode: null }
    },
    dateTime: { defaultTimeZone: null }
  });

  useEffect(() => {
    if (supportedEventTypesStore) {
      setProducts(supportedEventTypesStore);
    }
  }, [supportedEventTypesStore]);

  useEffect(() => {
    if (settingsMetaDataStore) {
      setSettingsMetaData(settingsMetaDataStore);
    }
  }, [settingsMetaDataStore]);

  const validate = (saveAttempted = false, settingsMetaDataUpdate) => {
    let toValidate;
    if (saveAttempted) {
      toValidate = settingsMetaData;
    } else {
      toValidate = merge(
        {},
        settingsDataModel,
        settingsMetaDataUpdate || settingsMetaData
      );
    }

    const validation = deviceContextSettingsValidator.validate(toValidate, {
      abortEarly: false,
      allowUnknown: true
    });
    let errors = formController.getFormErrors(validation, { saveAttempted });

    setValidationErrors(errors);
    return errors;
  };

  const saveSettingsMetaData = () => {
    const errors = validate(true);

    if (errors.meta.clean) {
      dispatch(updateSettingsMetaData(settingsMetaData));
      dispatch(getSupportedEventTypes());
    }
  };

  const handleChange = event => {
    let deviceContextSettingsUpdate;
    if (event.target.name === DEFAULT_TIME_ZONE_PATH && !event.target.value) {
      deviceContextSettingsUpdate = produce(settingsMetaData, draft => {
        set(draft, event.target.name, settingsMetaData?.dateTime?.defaultTimeZone);
      });
    } else {
      deviceContextSettingsUpdate = produce(settingsMetaData, draft => {
        set(draft, event.target.name, event.target.value);
      });
    }
    if (event.target.name === DEFAULT_TIME_ZONE_PATH && currentTimeZone !== event.target.value) {
      dispatch(setTimeZoneModified());
    } else if (event.target.name === DEFAULT_TIME_ZONE_PATH && isTimeZoneModified) {
      dispatch(unsetTimeZoneModified());
    }

    setSettingsMetaData(deviceContextSettingsUpdate);
    validate(false, deviceContextSettingsUpdate);
  };

  const onBlur = names => {
    names = Array.isArray(names) ? names : [names];
    let events = names.map(name => ({ target: { name } }));
    events.forEach(e => formController.onBlur(e));
    validate();
  };

  const onFocus = names => {
    names = Array.isArray(names) ? names : [names];
    let events = names.map(name => ({ target: { name } }));
    events.forEach(e => formController.onFocus(e));
    validate();
  };

  const shouldShowError = paths => {
    return formController.shouldShowError(validationErrors, paths);
  };

  const getErrorMessage = paths => {
    return formController.getErrorMessage(validationErrors, paths);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  return {
    isLoading,
    isSettingsMetaDataUpdating,
    isSettingsMetaDataUpdated,
    settingsMetaDataStore,
    settingsMetaData,
    DEFAULT_TIME_ZONE_PATH,
    CHECK_IN_TIME,
    CHECK_OUT_TIME,
    PRODUCT_INTEGRATIONS,
    CONSENT_PROMOTIONAL,
    CONSENT_TRANSACTIONAL,
    ENABLE_UTM_TRACKING,
    SMS_COUNTRY_CODE,
    LANGUAGE_CODE,
    handleChange,
    saveSettingsMetaData,
    onFocus,
    onBlur,
    products,
    shouldShowError,
    showFormSaveError,
    getErrorMessage
  };
}
